// React/finance-app/src/features/tt-insights/tabs/NewModelMetricsTab.jsx
import React from 'react';
import * as UI from '../components';

const common = UI.commonStyles || {
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  colors: {
    primary: '#0d6efd',
    success: '#198754',
    info: '#0dcaf0',
    warning: '#ffc107',
    light: '#f8f9fa',
    muted: '#6c757d',
    dark: '#212529',
  },
};

const Card =
  UI.Card ||
  function FallbackCard({ children, title, icon }) {
    return (
      <div style={{ border: '1px solid #eee', padding: '1rem', marginBottom: '1rem', background: '#fff' }}>
        {(title || icon) && (
          <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
            {icon} {title}
          </div>
        )}
        {children}
      </div>
    );
  };

const SectionHeader =
  UI.SectionHeader ||
  function FallbackHeader({ icon, title, subtitle }) {
    return (
      <div style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
        <div style={{ fontSize: '1rem', fontWeight: 600 }}>
          {icon} {title}
        </div>
        {subtitle && <div style={{ fontSize: '0.75rem', color: '#666' }}>{subtitle}</div>}
      </div>
    );
  };

const LoadingState =
  UI.LoadingState ||
  function FallbackLoading({ message }) {
    return <div style={{ padding: '1rem' }}>{message || 'Loading...'}</div>;
  };

const EmptyState =
  UI.EmptyState ||
  function FallbackEmpty({ title, description }) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#777' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{title}</div>
        {description && <div style={{ fontSize: '0.75rem' }}>{description}</div>}
      </div>
    );
  };

const StatsGrid =
  UI.StatsGrid ||
  function FallbackStatsGrid({ stats }) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        {stats.map((s, i) => (
          <Card key={i} title={s.label} icon={s.icon}>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: s.color || common.colors.primary }}>
              {s.value}
            </div>
            {s.hint && (
              <div style={{ fontSize: '0.75rem', color: common.colors.muted, marginTop: '0.5rem' }}>
                {s.hint}
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

const NewModelMetricsTab = ({ loading, newModelMetrics, userCode, fetchNewModelMetrics }) => {
  if (loading) {
    return <LoadingState message="Loading new model metrics..." />;
  }

  if (!userCode) {
    return (
      <div style={{ padding: common.spacing.lg }}>
        <SectionHeader
          icon="📊"
          title="New Model Metrics"
          subtitle="Metrics calculated using the 'New Model' filter (model = 'New')"
        />
        <Card>
          <EmptyState
            icon="📊"
            title="No User Code"
            description="Enter a user code in the controls above and click 'Get New Model Metrics'"
          />
        </Card>
      </div>
    );
  }

  const stats = newModelMetrics
    ? [
        {
          icon: '💰',
          label: 'Total Rebate',
          value: `£${(newModelMetrics.total_rebate || 0).toFixed(2)}`,
          color: common.colors.success,
          hint: 'Sum of all-time referral + trust rebate (new model only)',
        },
        {
          icon: '📈',
          label: 'ROI',
          value: `${(newModelMetrics.roi || 0).toFixed(4)}%`,
          color: common.colors.info,
          hint: 'Return on Investment percentage',
        },
        {
          icon: '🏆',
          label: 'Rank',
          value: newModelMetrics.rank ?? 'N/A',
          color: common.colors.warning,
          hint: 'User rebate-based rank (new model filter)',
        },
        {
          icon: '💸',
          label: 'Withdrawals',
          value: `£${(newModelMetrics.new_model_withdrawals || 0).toFixed(2)}`,
          color: common.colors.primary,
          hint: 'Total withdrawals under new model',
        },
      ]
    : [];

  return (
    <div style={{ padding: common.spacing.lg }}>
      <SectionHeader
        icon="📊"
        title="New Model Metrics"
        subtitle="Metrics calculated using the 'New Model' filter (model = 'New')"
      />

      {!newModelMetrics ? (
        <Card>
          <EmptyState
            icon="📊"
            title="No Metrics Available"
            description={`Click 'Get New Model Metrics' button above to fetch metrics for user code: ${userCode}`}
          />
        </Card>
      ) : (
        <>
          <StatsGrid stats={stats} />

          <Card title="📋 Details" icon="ℹ️">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: common.spacing.md }}>
              <div>
                <strong>Total Rebate:</strong>
                <div style={{ fontSize: '1.2rem', color: common.colors.success, marginTop: '0.25rem' }}>
                  £{(newModelMetrics.total_rebate || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '0.75rem', color: common.colors.muted, marginTop: '0.25rem' }}>
                  Calculated as the sum of all-time referral rebate + all-time trust rebate, with new model filter applied
                </div>
              </div>
              <div>
                <strong>ROI:</strong>
                <div style={{ fontSize: '1.2rem', color: common.colors.info, marginTop: '0.25rem' }}>
                  {(newModelMetrics.roi || 0).toFixed(4)}%
                </div>
                <div style={{ fontSize: '0.75rem', color: common.colors.muted, marginTop: '0.25rem' }}>
                  Formula: ((Pending Referral + Paid Referral + Pending Trust + Paid Trust) / User Contribution) × 100
                </div>
              </div>
              <div>
                <strong>Rank:</strong>
                <div style={{ fontSize: '1.2rem', color: common.colors.warning, marginTop: '0.25rem' }}>
                  {newModelMetrics.rank ?? 'N/A'}
                </div>
                <div style={{ fontSize: '0.75rem', color: common.colors.muted, marginTop: '0.25rem' }}>
                  User's rebate-based rank (calculated with new model filter applied)
                </div>
              </div>
              <div>
                <strong>Withdrawals:</strong>
                <div style={{ fontSize: '1.2rem', color: common.colors.primary, marginTop: '0.25rem' }}>
                  £{(newModelMetrics.new_model_withdrawals || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '0.75rem', color: common.colors.muted, marginTop: '0.25rem' }}>
                  Total withdrawals processed under the new model filter
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default NewModelMetricsTab;

